import * as THREE from 'three';

type DustParticle = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
};

export class FpsBuildDustVfx {
  private readonly particles: DustParticle[] = [];
  private readonly geo = new THREE.SphereGeometry(0.12, 5, 5);
  private readonly colors = [0xeeeeee, 0xdddddd, 0xcccccc, 0xe5e5e5];

  constructor(private readonly scene: THREE.Scene) {}

  burst(x: number, y: number, z: number, kind: string): void {
    let count = 14;
    let radius = 1.45;

    // Adjust particle count and ring radius based on build kind
    if (kind === 'ramp' || kind === 'floor') {
      count = 18;
      radius = 1.6;
    }

    const baseHeight = kind === 'wall' ? -1.35 : -0.05;

    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const mat = new THREE.MeshBasicMaterial({
        color: this.colors[i % this.colors.length]!,
        transparent: true,
        opacity: 0.65,
        depthWrite: false,
      });

      const mesh = new THREE.Mesh(this.geo, mat);
      
      // Position particles in a ring at the base/center of the block
      const startX = x + Math.cos(angle) * radius * (0.95 + Math.random() * 0.1);
      const startY = y + baseHeight + (Math.random() - 0.5) * 0.15;
      const startZ = z + Math.sin(angle) * radius * (0.95 + Math.random() * 0.1);
      mesh.position.set(startX, startY, startZ);
      
      // Random scale for a natural puffy look
      mesh.scale.setScalar(0.7 + Math.random() * 0.8);
      this.scene.add(mesh);

      // Expand outward from the center
      const speed = 1.8 + Math.random() * 1.5;
      const vel = new THREE.Vector3(
        Math.cos(angle) * speed,
        0.35 + Math.random() * 0.75,
        Math.sin(angle) * speed,
      );

      this.particles.push({
        mesh,
        vel,
        life: 0,
        maxLife: 0.38 + Math.random() * 0.22,
      });
    }
  }

  update(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.life += dt;
      
      p.mesh.position.addScaledVector(p.vel, dt);
      
      // Slow down and float slightly upwards
      p.vel.y += dt * 0.4;
      p.vel.multiplyScalar(1 - dt * 2.8);

      const ratio = p.life / p.maxLife;
      const mat = p.mesh.material as THREE.MeshBasicMaterial;
      
      // Fade out nicely
      mat.opacity = Math.max(0, 0.65 * (1 - ratio * ratio));
      
      // Expand and then shrink/disappear
      const scale = (0.7 + ratio * 1.6) * (1 - ratio * 0.35);
      p.mesh.scale.setScalar(scale);

      if (p.life >= p.maxLife) {
        this.scene.remove(p.mesh);
        mat.dispose();
        this.particles.splice(i, 1);
      }
    }
  }

  dispose(): void {
    for (const p of this.particles) {
      this.scene.remove(p.mesh);
      (p.mesh.material as THREE.Material).dispose();
    }
    this.particles.length = 0;
    this.geo.dispose();
  }
}
